import React, { useState, useMemo } from 'react';
import { Modal, Button, Table, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { urlSegura } from '../../utils/urlSegura';

/**
 * TIN-23 CA-04: Modal de selección de proveedor ganador. Solo muestra
 * proveedores con cotización registrada. La selección es atómica e
 * irreversible: descarta a los demás y expira sus tokens.
 *
 * @param {boolean} show
 * @param {() => void} onHide
 * @param {Array} proveedoresAsignados - del documento Requisicion
 * @param {(proveedorAsignadoId: string) => Promise<void> | void} onConfirmar
 */
const SeleccionarGanadorModal = ({ show, onHide, proveedoresAsignados = [], onConfirmar }) => {
  const [seleccionId, setSeleccionId] = useState(null);
  const [enviando, setEnviando] = useState(false);

  // TIN-23 iter 3: candidato = proveedor activo con cotización válida
  // (archivo, link a propuesta en línea, o correo de envío).
  const candidatos = useMemo(
    () =>
      proveedoresAsignados.filter(
        (pa) =>
          !['Rechazado', 'Descartado'].includes(pa.estadoEnvio) &&
          pa?.cotizacion &&
          (pa.cotizacion.archivo || pa.cotizacion.linkPropuesta || pa.cotizacion.correoEnvio)
      ),
    [proveedoresAsignados]
  );

  const handleConfirmar = async () => {
    if (!seleccionId) {
      Swal.fire({
        icon: 'warning',
        title: 'Selecciona un proveedor',
        text: 'Debes elegir el proveedor ganador antes de confirmar.',
      });
      return;
    }
    const confirma = await Swal.fire({
      icon: 'warning',
      title: '¿Confirmar selección?',
      html: '<p>Esta acción es <strong>irreversible</strong>. Los demás proveedores perderán acceso a su portal y verán un mensaje de cortesía.</p>',
      showCancelButton: true,
      confirmButtonText: 'Sí, confirmar ganador',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0d6efd',
    });
    if (!confirma.isConfirmed) return;

    try {
      setEnviando(true);
      await onConfirmar(seleccionId);
      setSeleccionId(null);
      onHide();
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error al seleccionar ganador',
        text: e?.response?.data?.message || e?.message || 'No se pudo completar la selección.',
      });
    } finally {
      setEnviando(false);
    }
  };

  const handleHide = () => {
    if (enviando) return;
    setSeleccionId(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!enviando}>
        <Modal.Title>Seleccionar proveedor ganador</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {candidatos.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            No hay proveedores con cotización registrada todavía.
          </Alert>
        ) : (
          <>
            <p className="text-muted small mb-3">
              Compara las cotizaciones y selecciona al ganador. Los demás proveedores
              quedarán automáticamente <strong>descartados</strong> y sus tokens públicos
              serán invalidados.
            </p>
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th style={{ width: 40 }} />
                  <th>Proveedor</th>
                  <th>Estado</th>
                  <th>Cotización</th>
                  <th>Observación</th>
                </tr>
              </thead>
              <tbody>
                {candidatos.map((pa) => (
                  <tr
                    key={pa._id}
                    onClick={() => !enviando && setSeleccionId(pa._id)}
                    style={{
                      cursor: enviando ? 'not-allowed' : 'pointer',
                      backgroundColor: seleccionId === pa._id ? '#e7f5ff' : undefined,
                    }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="radio"
                        name="ganador"
                        checked={seleccionId === pa._id}
                        onChange={() => setSeleccionId(pa._id)}
                        disabled={enviando}
                      />
                    </td>
                    <td>{pa.proveedor?.nombre || '—'}</td>
                    <td>{pa.estadoEnvio}</td>
                    <td>
                      {(() => {
                        // TIN-23: hrefs validados con urlSegura (defensa contra javascript:/data:)
                        const safeArchivo = urlSegura(pa.cotizacion?.archivo);
                        const safeLink = urlSegura(pa.cotizacion?.linkPropuesta);
                        if (safeArchivo) {
                          return (
                            <a href={safeArchivo} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                              📎 Ver archivo
                            </a>
                          );
                        }
                        if (safeLink) {
                          return (
                            <a href={safeLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                              🔗 Ver propuesta
                            </a>
                          );
                        }
                        if (pa.cotizacion?.correoEnvio) {
                          return (
                            <small>
                              ✉ Enviada al correo
                              <br />
                              <code style={{ fontSize: '0.75rem' }}>{pa.cotizacion.correoEnvio}</code>
                            </small>
                          );
                        }
                        return '—';
                      })()}
                    </td>
                    <td>{pa.cotizacion?.observacion || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="small text-warning mb-0">
              ⚠ Esta acción es irreversible.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmar}
          disabled={enviando || !seleccionId || candidatos.length === 0}
        >
          {enviando ? 'Procesando...' : 'Confirmar selección'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SeleccionarGanadorModal;
